import axios from 'axios';
import { offlineDB } from './offlineDB';
import { toast } from 'sonner';

import API from './api';

class SyncManager {
    constructor() {
        this.isSyncing = false;
        this.syncInterval = null;
        this.setupEventListeners();
    }

    setupEventListeners() {
        // Sincronizar cuando vuelva la conexión
        window.addEventListener('connection-restored', () => {
            console.log('🔄 Conexión restaurada - Sincronizando datos pendientes...');
            setTimeout(() => this.syncAll(), 2000);
        });

        // Sincronización periódica cada 5 minutos (si hay internet)
        this.syncInterval = setInterval(() => {
            if (navigator.onLine) {
                this.syncAll();
            }
        }, 5 * 60 * 1000);
    }

    async syncAll() {
        if (this.isSyncing) {
            console.log('⏳ Sincronización ya en progreso...');
            return { success: true, message: 'Ya sincronizando' };
        }

        this.isSyncing = true;

        try {
            const results = {
                orders: { success: 0, failed: 0 },
                errors: []
            };

            // 1. Sincronizar pedidos pendientes
            const orderResult = await this.syncPendingOrders();
            results.orders = orderResult;

            // 2. Actualizar cache de menú
            await this.updateMenuCache();

            this.isSyncing = false;

            // Mostrar resultado
            if (results.orders.success > 0) {
                toast.success(`✅ ${results.orders.success} pedido(s) sincronizado(s)`);
            }

            if (results.orders.failed > 0) {
                toast.error(`❌ ${results.orders.failed} pedido(s) fallaron`);
            }

            console.log('✅ Sincronización completada:', results);
            return results;

        } catch (error) {
            console.error('❌ Error en sincronización:', error);
            this.isSyncing = false;
            return { success: false, error: error.message };
        }
    }

    async syncPendingOrders() {
        const results = { success: 0, failed: 0, errors: [] };

        try {
            const pendingOrders = await offlineDB.getPendingOrders();

            if (pendingOrders.length === 0) {
                console.log('📭 No hay pedidos pendientes para sincronizar');
                return results;
            }

            console.log(`📤 Sincronizando ${pendingOrders.length} pedido(s)...`);

            for (const order of pendingOrders) {
                try {
                    // TENANT CHECK: Avoid syncing data from another company
                    // Get current user info from localStorage or Context
                    const storedUser = localStorage.getItem('user');
                    let currentUserCompanyId = null;
                    if (storedUser) {
                        try {
                            const userObj = JSON.parse(storedUser);
                            currentUserCompanyId = userObj.company_id;
                        } catch (e) { console.error("Error parsing user for sync check", e); }
                    }

                    if (order.company_id && currentUserCompanyId && order.company_id !== currentUserCompanyId) {
                        console.warn(`🔒 Skipping sync for order ${order.id} (Company Mismatch: ${order.company_id} != ${currentUserCompanyId})`);
                        continue;
                    }

                    // Intentar enviar al backend
                    // Use standard axios instance
                    const response = await axios.post(`${API}/orders/pos`, order.body || order.data, {
                        withCredentials: true,
                        timeout: 10000
                    });

                    if (response.data) {
                        // Marcar como sincronizado
                        await offlineDB.markOrderAsSynced(order.id);
                        results.success++;
                        console.log(`✅ Pedido ${order.id} sincronizado correctamente`);
                    }

                } catch (error) {
                    results.failed++;
                    results.errors.push({
                        orderId: order.id,
                        error: error.message
                    });
                    console.error(`❌ Error sincronizando pedido ${order.id}:`, error.message);

                    // Si el error es de autenticación o datos, marcar como fallido
                    if (error.response?.status === 401 || error.response?.status === 400 || error.response?.status === 403) {
                        console.warn(`⚠️ Pedido ${order.id} tiene datos inválidos o auth falló, se marcará como sincronizado para evitar bucle`);
                        await offlineDB.markOrderAsSynced(order.id);
                    }
                }
            }

            // Limpiar pedidos sincronizados antiguos (más de 7 días)
            await offlineDB.deleteSyncedOrders();

            return results;

        } catch (error) {
            console.error('Error obteniendo pedidos pendientes:', error);
            return results;
        }
    }

    async updateMenuCache() {
        try {
            // Obtener menú actualizado
            const [menuRes, categoriesRes] = await Promise.all([
                axios.get(`${API}/menu`, { timeout: 5000 }),
                axios.get(`${API}/categories`, { timeout: 5000 })
            ]);

            if (menuRes.data) {
                await offlineDB.cacheMenu(menuRes.data);
                console.log(`✅ Cache de menú actualizado (${menuRes.data.length} items)`);
            }

            if (categoriesRes.data) {
                await offlineDB.cacheCategories(categoriesRes.data);
                console.log(`✅ Cache de categorías actualizado (${categoriesRes.data.length} categorías)`);
            }

            return true;
        } catch (error) {
            console.warn('⚠️ No se pudo actualizar cache de menú:', error.message);
            return false;
        }
    }

    async getStats() {
        const dbStats = await offlineDB.getStats();
        return {
            ...dbStats,
            isSyncing: this.isSyncing,
            isOnline: navigator.onLine
        };
    }

    destroy() {
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
        }
    }
}

// Singleton instance
export const syncManager = new SyncManager();

// Helper para uso manual
export const triggerSync = () => syncManager.syncAll();
